import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Search, Users, Music, Video, Radio, Calendar, MapPin, Play } from 'lucide-react';
import { usePlayer } from '../context/PlayerContext';
import { motion } from 'framer-motion';
import { StageEmptyState } from '../components/StageEmptyState';
import { API } from '../config/api';



export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState({ artists: [], tracks: [], videos: [], channels: [], events: [] });
  const [loading, setLoading] = useState(false);
  const { playTrack } = usePlayer();

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setQuery(q);
      performSearch(q);
    }
  }, [searchParams]);

  const performSearch = async (q) => {
    if (!q.trim()) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/search?q=${encodeURIComponent(q)}`);
      if (response.ok) setResults(await response.json());
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      setSearchParams({ q: query });
    }
  };

  const totalResults = 
    results.artists.length + 
    results.tracks.length + 
    results.videos.length + 
    results.events.length;

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="search-page">
      <div className="max-w-[1800px] mx-auto">
        {/* Search Header */}
        <div className="mb-8">
          <h1 className="font-heading text-4xl font-bold uppercase mb-4">Search</h1>
          <form onSubmit={handleSearch} className="max-w-2xl">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search artists, tracks, videos, events..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-12 h-14 text-lg bg-muted/50 border-border"
                data-testid="search-input"
              />
            </div>
          </form>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : searchParams.get('q') ? (
          <>
            <p className="text-muted-foreground mb-6">
              {totalResults} results for "{searchParams.get('q')}"
            </p>

            <Tabs defaultValue="all">
              <TabsList className="bg-muted/50 mb-6">
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="artists">
                  <Users className="w-4 h-4 mr-2" />
                  Artists ({results.artists.length})
                </TabsTrigger>
                <TabsTrigger value="tracks">
                  <Music className="w-4 h-4 mr-2" />
                  Tracks ({results.tracks.length})
                </TabsTrigger>
                <TabsTrigger value="videos">
                  <Video className="w-4 h-4 mr-2" />
                  Videos ({results.videos.length})
                </TabsTrigger>
                <TabsTrigger value="events">
                  <Calendar className="w-4 h-4 mr-2" />
                  Events ({results.events.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="all">
                <div className="space-y-8">
                  {results.artists.length > 0 && (
                    <section>
                      <h2 className="font-heading text-xl font-bold uppercase mb-4">Artists</h2>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                        {results.artists.slice(0, 6).map((artist) => (
                          <Link key={artist.artist_id} to={`/artist/${artist.artist_id}`}>
                            <Card className="glass-card border-border overflow-hidden hover-lift">
                              <div className="aspect-square bg-muted">
                                {artist.avatar_url ? (
                                  <img src={artist.avatar_url} alt={artist.stage_name} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full bg-gradient-to-br from-primary/30 to-secondary/30 flex items-center justify-center">
                                    <Users className="w-8 h-8 text-muted-foreground" />
                                  </div>
                                )}
                              </div>
                              <div className="p-3">
                                <p className="font-medium truncate">{artist.stage_name}</p>
                                {artist.neighborhood && (
                                  <p className="text-xs text-muted-foreground">{artist.neighborhood}</p>
                                )}
                              </div>
                            </Card>
                          </Link>
                        ))}
                      </div>
                    </section>
                  )}

                  {results.tracks.length > 0 && (
                    <section>
                      <h2 className="font-heading text-xl font-bold uppercase mb-4">Tracks</h2>
                      <div className="space-y-2">
                        {results.tracks.slice(0, 5).map((track, index) => (
                          <motion.div
                            key={track.track_id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="track-row flex items-center gap-4 p-3 rounded-lg cursor-pointer"
                            onClick={() => playTrack(track, results.tracks)}
                          >
                            <div className="w-12 h-12 bg-muted rounded overflow-hidden relative flex-shrink-0">
                              <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                                <Music className="w-5 h-5 text-muted-foreground" />
                              </div>
                              <div className="play-icon absolute inset-0 bg-background/50 flex items-center justify-center">
                                <Play className="w-5 h-5 text-white" fill="white" />
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{track.title}</p>
                              <p className="text-sm text-muted-foreground">{track.artist_name}</p>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </section>
                  )}

                  {results.events.length > 0 && (
                    <section>
                      <h2 className="font-heading text-xl font-bold uppercase mb-4">Events</h2>
                      <div className="grid gap-4">
                        {results.events.slice(0, 3).map((event) => (
                          <Link key={event.event_id} to={`/events/${event.event_id}`}>
                            <Card className="glass-card border-border p-4 hover-lift">
                              <div className="flex items-center gap-4">
                                <Badge className="bg-secondary">{event.event_type}</Badge>
                                <div className="flex-1">
                                  <h3 className="font-medium">{event.title}</h3>
                                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    {event.venue} • {event.neighborhood}
                                  </p>
                                </div>
                              </div>
                            </Card>
                          </Link>
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="artists">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {results.artists.map((artist) => (
                    <Link key={artist.artist_id} to={`/artist/${artist.artist_id}`}>
                      <Card className="glass-card border-border overflow-hidden hover-lift">
                        <div className="aspect-square bg-muted">
                          {artist.avatar_url ? (
                            <img src={artist.avatar_url} alt={artist.stage_name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-primary/30 to-secondary/30 flex items-center justify-center">
                              <Users className="w-8 h-8 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          <p className="font-medium truncate">{artist.stage_name}</p>
                          {artist.neighborhood && (
                            <p className="text-xs text-muted-foreground">{artist.neighborhood}</p>
                          )}
                        </div>
                      </Card>
                    </Link>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="tracks">
                <div className="space-y-2">
                  {results.tracks.map((track, index) => (
                    <div
                      key={track.track_id}
                      className="track-row flex items-center gap-4 p-3 rounded-lg cursor-pointer"
                      onClick={() => playTrack(track, results.tracks)}
                    >
                      <span className="w-6 text-center text-sm text-muted-foreground">{index + 1}</span>
                      <div className="w-12 h-12 bg-muted rounded flex items-center justify-center">
                        <Music className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{track.title}</p>
                        <p className="text-sm text-muted-foreground">{track.artist_name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="videos">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {results.videos.map((video) => (
                    <Card key={video.video_id} className="glass-card border-border overflow-hidden hover-lift">
                      <div className="aspect-video bg-muted flex items-center justify-center">
                        <Video className="w-12 h-12 text-muted-foreground" />
                      </div>
                      <div className="p-4">
                        <p className="font-medium truncate">{video.title}</p>
                        <p className="text-sm text-muted-foreground">{video.artist_name}</p>
                      </div>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="events">
                <div className="grid gap-4">
                  {results.events.map((event) => (
                    <Link key={event.event_id} to={`/events/${event.event_id}`}>
                      <Card className="glass-card border-border p-4 hover-lift">
                        <div className="flex items-center gap-4">
                          <Badge className="bg-secondary">{event.event_type}</Badge>
                          <div className="flex-1">
                            <h3 className="font-medium">{event.title}</h3>
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {event.venue} • {event.neighborhood}
                            </p>
                          </div>
                        </div>
                      </Card>
                    </Link>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <StageEmptyState 
            variant="search" 
            customHeadline="Search DMV Soundstage"
            customHint="Find artists, tracks, videos, and events"
            customAction={null}
          />
        )}
      </div>
    </div>
  );
}
